import { toNestErrors } from '@hookform/resolvers';
import type {
  FieldError,
  FieldValues,
  Resolver,
  ResolverOptions,
} from 'react-hook-form';
import * as Yup from 'yup';
import type { ObjectSchema, AnyObject } from 'yup';

/**
 * True when {@link path} is one of {@link validatedFields}, or a nested path under it
 * (e.g. `conversion_factors.kg` under `conversion_factors`).
 */
const isValidatedPath = (
  path: string,
  validatedFields: readonly string[],
): boolean =>
  validatedFields.some(
    (field) =>
      path === field ||
      path.startsWith(`${field}.`) ||
      path.startsWith(`${field}[`),
  );

/**
 * Flattens a Yup {@link Yup.ValidationError} into path → {@link FieldError},
 * matching `@hookform/resolvers` yupResolver's first-error-per-path behavior.
 */
const parseYupError = (
  error: Yup.ValidationError,
): Record<string, FieldError> => {
  const issues = error.inner.length > 0 ? error.inner : [error];
  const result: Record<string, FieldError> = {};

  for (const issue of issues) {
    if (!issue.path || result[issue.path]) {
      continue;
    }
    result[issue.path] = {
      type: issue.type ?? 'validation',
      message: issue.message,
    };
  }

  return result;
};

/**
 * Creates a react-hook-form {@link Resolver} that validates form values against a Yup
 * {@link ObjectSchema}, then returns Yup's transformed values on success.
 *
 * Behavior is aligned with `yupResolver` from `@hookform/resolvers`: nested errors are
 * nested for RHF Controllers, and schema transforms (e.g. `.trim()`) are applied.
 * Only errors under {@link validatedFields} (including nested paths) are surfaced.
 * If Yup fails but every error is filtered out, a root-level error is returned so RHF
 * does not treat the result as success with empty values.
 *
 * @param schema - Yup object schema covering at least the validated fields.
 * @param validatedFields - Field roots whose validation errors should be surfaced.
 */
export const createYupResolver = <TFormValues extends FieldValues>(
  schema: ObjectSchema<AnyObject>,
  validatedFields: readonly (keyof TFormValues & string)[],
): Resolver<TFormValues> => {
  return async (values, _context, options: ResolverOptions<TFormValues>) => {
    try {
      // Validate full values so Yup transforms match previous yupResolver behavior
      const validated = await schema.validate(values, { abortEarly: false });
      return {
        values: { ...values, ...validated },
        errors: {},
      };
    } catch (error) {
      if (!(error instanceof Yup.ValidationError)) {
        throw error;
      }

      const flatErrors = Object.fromEntries(
        Object.entries(parseYupError(error)).filter(([path]) =>
          isValidatedPath(path, validatedFields),
        ),
      );

      // Avoid false success ({ values: {}, errors: {} }) when all issues were filtered out
      if (Object.keys(flatErrors).length === 0) {
        flatErrors.root = {
          type: 'validation',
          message: error.message || 'Validation failed',
        };
      }

      return {
        values: {},
        errors: toNestErrors(flatErrors, options),
      };
    }
  };
};
